// =============================================================================
// Admin Analytics — shared UI helpers, constants, and contract conversions
// =============================================================================
import { format, subDays } from "date-fns";

import { CalendarDateRange } from "@fronterahealth/frontera-ui-components";

import { DateRangeContract, ReportStatus } from "@pages/AdminAnalytics/data/types";
// Reuse the Video Analytics primitives rather than duplicating them.
import { DATE_FORMAT } from "@pages/VideoAnalytics/helpers";

export { calculatePercentageChange } from "@pages/VideoAnalytics/helpers";

export const ADMIN_ANALYTICS_TITLES = {
  TOTAL_REPORTS: "Total Reports",
  REPORTS_BY_STATUS: "Reports by Status",
  REPORTS_BY_USER: "Reports by User",
  SUBMISSION_TREND: "Submission Trend",
  AVG_WRITING_TIME: "Avg. Report Writing Time",
  OVERDUE_REPORTS: "Overdue Reports",
  OPEN_QA_FLAGS: "Open QA Flags",
  RESOLVED_QA_FLAGS: "Resolved QA Flags",
  QA_BY_USER: "QA Flags by User",
  INVITED: "Invited",
  INVITES_EXPIRED: "Invites Expired",
  LOGGED_IN: "Logged In",
  NOT_YET_LOGGED_IN: "Not Yet Logged In",
} as const;

/** Canonical display order for all five report statuses. */
export const ALL_REPORT_STATUSES: ReportStatus[] = ["draft", "complete", "submitted", "needs_revision", "approved"];

/** Statuses shown for orgs not connected to the Catalight submission pipeline. */
export const BASE_REPORT_STATUSES: ReportStatus[] = ["draft", "complete"];

/** Status colors — drawn from the existing analytics palette (warm-aligned). */
export const STATUS_COLORS: Record<ReportStatus, string> = {
  draft: "#C9C2B5",
  complete: "#14B8A6",
  submitted: "#3B82F6",
  needs_revision: "#F97316",
  approved: "#15803D",
};

/** Definitions shown on hover in the status chart. */
export const STATUS_DEFINITIONS: Record<ReportStatus, string> = {
  draft: "This report is still in progress and hasn't been completed yet.",
  complete:
    "This report has been finished. For most reports, no further action is needed. If this report is linked to a payor integration, it's ready to submit.",
  submitted:
    "(Payor-integrated reports only) This report has been sent to your payor network and is currently under review.",
  needs_revision:
    "(Payor-integrated reports only) Your payor has reviewed this report and returned feedback. Revisions are required before resubmitting.",
  approved:
    "(Payor-integrated reports only) Your payor has approved this report. Goals and targets are ready to sync to your EHR.",
};

/** Format whole minutes into "Xhr Ymin" (writing-time estimate). */
export const formatMinutesToHrMin = (minutes: number): string => {
  if (!minutes) return "0min";
  const hr = Math.floor(minutes / 60);
  const min = Math.round(minutes % 60);
  if (hr === 0) return `${min}min`;
  if (min === 0) return `${hr}hr`;
  return `${hr}hr ${min}min`;
};

/** Percent string for resolution rates etc. */
export const formatPercent = (ratio: number): string => `${Math.round(ratio * 100)}%`;

/**
 * Client-side CSV export of an arbitrary row set. `fields` doubles as the column
 * projection: each field's `label` becomes a header and `get(row)` its cell.
 * Used by the drilldown dialogs: the server drilldowns hand it every row of the
 * current result (see `useDrilldownExport`), the client-side ones their filtered rows.
 */
export const exportRowsToCsv = <T>(
  fileName: string,
  fields: Array<{ label: string; get: (row: T) => string }>,
  rows: T[],
): void => {
  const escape = (cell: string): string => `"${cell.replace(/"/g, '""')}"`;
  const header = fields.map((f) => escape(f.label)).join(",");
  const body = rows.map((row) => fields.map((f) => escape(f.get(row) ?? "")).join(",")).join("\r\n");
  const csv = `${header}\r\n${body}`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const DEFAULT_DAYS = 90;
/** Use a built-in CalendarPicker preset so the label round-trips through the picker. */
export const DEFAULT_RANGE_LABEL = "Last 3 Months" as const;

/** The dashboard's default global range: last ~3 months (wide enough that every widget shows data). */
export const defaultCalendarRange = (): CalendarDateRange => {
  const today = new Date();
  return {
    startDate: subDays(today, DEFAULT_DAYS - 1),
    endDate: today,
    selectedLabel: DEFAULT_RANGE_LABEL,
  };
};

/**
 * Convert a Mosaic `CalendarDateRange` into the absolute, ISO-string range the
 * adapter (and eventually the BE) speaks. Falls back to the last 7 days when the
 * picker hasn't produced a range yet.
 */
export const toRangeContract = (range: CalendarDateRange | undefined): DateRangeContract => {
  const start = range?.startDate ?? subDays(new Date(), DEFAULT_DAYS - 1);
  const end = range?.endDate ?? new Date();
  return {
    start: format(start, DATE_FORMAT),
    end: format(end, DATE_FORMAT),
    label: range?.selectedLabel ?? undefined,
  };
};

/** Convert the comparison half of a `CalendarDateRange`, when present. */
export const toComparisonContract = (range: CalendarDateRange | undefined): DateRangeContract | undefined => {
  if (!range?.comparisonStartDate || !range?.comparisonEndDate) return undefined;
  return {
    start: format(range.comparisonStartDate, DATE_FORMAT),
    end: format(range.comparisonEndDate, DATE_FORMAT),
    label: range.selectedComparisonLabel ?? undefined,
  };
};

/**
 * Humanize a backend `templateGroup` value (e.g. "ABC_STANDARD") for display.
 * Template names are org-defined, so there's no fixed label map — this just
 * turns snake/upper case into title case.
 */
export const formatTemplateName = (name: string): string =>
  name
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Build the `/members/provider/:providerId` URL for an analytics row. Analytics
 * rows carry the provider as a `ProviderType` relay id — the same id the
 * members route's `providerById` lookup takes — so it passes straight through.
 */
export const providerDetailUrl = (providerGlobalId: string): string => `/members/provider/${providerGlobalId}`;

/** Build the immutable React Query key fragment for a resolved range + template filters. */
export const rangeKey = (
  range: CalendarDateRange | undefined,
  templateType: string[] | string = [],
  templateName: string[] | string = [],
): string => {
  const c = toRangeContract(range);
  const comp = toComparisonContract(range);
  const typeKey = Array.isArray(templateType)
    ? [...templateType].sort((a, b) => a.localeCompare(b)).join(",")
    : templateType;
  const nameKey = Array.isArray(templateName)
    ? [...templateName].sort((a, b) => a.localeCompare(b)).join(",")
    : templateName;
  return [c.start, c.end, comp?.start ?? "", comp?.end ?? "", typeKey, nameKey].join("|");
};
